import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, LogOut, ChevronDown, Check, Building2, Loader2, Terminal } from 'lucide-react';
// import { useTheme } from '../context/ThemeContext'; // dark mode disabled for now
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useTenant } from '../context/TenantContext';
import { resetUser } from '../utils/mixpanel';
import { setActiveTenantId } from '../services/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

const TOKEN_STORAGE_KEY = 'auth_token';
const SEARCH_FILTERS_STORAGE_KEY = 'search_v3_filters';
const LAST_DOMAIN_STORAGE_KEY = 'search_v3_last_domain';

function clearSessionAndRedirect() {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem('current_user');
    localStorage.removeItem('tid');
    localStorage.removeItem('own_tid');
    localStorage.removeItem(SEARCH_FILTERS_STORAGE_KEY);
    localStorage.removeItem(LAST_DOMAIN_STORAGE_KEY);
    setActiveTenantId(null);
    resetUser();
  } catch {
    // ignore
  }
  if (typeof window !== 'undefined') {
    window.location.href = '/login';
  }
}

interface UserMenuProps {
  className?: string;
  variant?: 'compact' | 'bordered';
}

const UserMenu: React.FC<UserMenuProps> = ({ className = '', variant = 'bordered' }) => {
  const { displayName } = useCurrentUser();
  // const { isDark, toggleTheme } = useTheme(); // dark mode disabled for now
  const { isAdmin, tenants, selectedTenantId, selectedTenantName, switchTenant, isSwitching } = useTenant();
  const [tenantOpen, setTenantOpen] = useState(false);
  const navigate = useNavigate();

  const showTenantSwitcher = isAdmin && tenants.length > 1;

  const handleTenantSelect = useCallback((tenantId: number) => {
    switchTenant(tenantId);
    setTenantOpen(false);
    navigate('/');
  }, [switchTenant, navigate]);

  const triggerVariant = variant === 'bordered' ? 'outline' : 'ghost';
  const triggerSize = variant === 'bordered' ? 'icon' : 'icon';

  return (
    <div className={className}>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant={triggerVariant}
            size={triggerSize}
            className={variant === 'bordered' ? 'h-9 w-9 rounded-md border' : 'h-9 w-9 rounded-lg'}
            aria-label="Open menu"
          >
            <Settings className="h-5 w-5" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 rounded-xl p-0 overflow-hidden">
          {/* Email display */}
          {displayName && (
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800">
              <p className="text-xs text-gray-600 dark:text-gray-400 truncate" title={displayName}>
                {displayName}
              </p>
            </div>
          )}

          {/* Tenant Switcher (collapsible) */}
          {showTenantSwitcher && (
            <div className="border-b border-gray-100 dark:border-gray-800">
              <button
                type="button"
                onClick={() => !isSwitching && setTenantOpen(prev => !prev)}
                disabled={isSwitching}
                className={`w-full flex items-center justify-between px-4 py-2.5 transition-colors ${
                  isSwitching ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-slate-800'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  {isSwitching
                    ? <Loader2 className="w-4 h-4 text-gray-400 dark:text-gray-500 animate-spin" />
                    : <Building2 className="w-4 h-4 text-gray-400 dark:text-gray-500" />}
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {selectedTenantName ?? 'Select tenant'}
                  </span>
                </div>
                <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${tenantOpen ? 'rotate-180' : ''}`} />
              </button>

              {tenantOpen && (
                <div className="pb-2">
                  <div className="max-h-48 overflow-y-auto">
                    {tenants.map(t => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => handleTenantSelect(t.id)}
                        disabled={isSwitching}
                        className={`w-full flex items-center justify-between px-4 py-2 text-sm transition-colors ${
                          isSwitching ? 'opacity-60 cursor-not-allowed' :
                          selectedTenantId === t.id
                            ? 'bg-gray-50 dark:bg-slate-800/50 text-gray-900 dark:text-gray-100 font-medium'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800'
                        }`}
                      >
                        <span className="text-left">{t.name}</span>
                        {selectedTenantId === t.id && <Check className="w-4 h-4 text-gray-900 dark:text-gray-100" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Admin Console (admin only) */}
          {isAdmin && (
            <div className="border-b border-gray-100 dark:border-gray-800">
              <button
                type="button"
                onClick={() => navigate('/admin')}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
              >
                <Terminal className="w-4 h-4 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                Admin Console
              </button>
            </div>
          )}

          {/* Theme toggle — disabled for now
          <div className="py-1">
            <button
              onClick={toggleTheme}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
            >
              {isDark ? (
                <>
                  <Sun className="w-4 h-4" aria-hidden="true" />
                  Light Mode
                </>
              ) : (
                <>
                  <Moon className="w-4 h-4" aria-hidden="true" />
                  Dark Mode
                </>
              )}
            </button>
          </div>
          */}
          {/* Sign Out */}
          <div className="py-1 border-t border-gray-100 dark:border-gray-800">
            <button
              onClick={() => clearSessionAndRedirect()}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <LogOut className="w-4 h-4" aria-hidden="true" />
              Sign Out
            </button>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default UserMenu;
